import React from 'react'
import palette from 'src/designSystem/variables/palette'
import { MIME_TYPE } from 'src/Utils/globalEnums'
import { getMimeTypeIcon } from 'src/components/ModalForms/FileType/useFileTypeIcon'
import { Spinner } from 'src/Throbber'
import {
  CsvV2ModalSubtitle,
  CsvV2Panel,
  CsvV2SampleBox,
  CsvV2DownloadTemplate,
  CsvV2SelectedState,
  CsvV2FileChip,
  CsvV2FileTypeIconWrapper,
  CsvV2FileNameText,
  CsvV2ChipRemoveButton,
  CsvV2CloseIcon,
  CsvV2ErrorList,
  CsvV2SummaryText,
  CsvV2SummaryValue,
  CsvV2EmptyUploadState,
  CsvV2UploadIcon,
  CsvV2BrowseButton,
  CsvV2SupportedText,
  CsvV2EmptyMessage,
} from './UploadCSVModalV2.styles'

export const CsvV2TemplatePanel = ({
  instruction,
  sampleText,
  onDownloadTemplate,
  downloadLabel = 'DOWNLOAD TEMPLATE',
}: {
  instruction: React.ReactNode
  sampleText: React.ReactNode
  onDownloadTemplate: () => void
  downloadLabel?: string
}): JSX.Element => (
  <CsvV2Panel>
    <CsvV2ModalSubtitle style={{ marginBottom: 0 }}>
      {instruction}
    </CsvV2ModalSubtitle>
    <CsvV2SampleBox>{sampleText}</CsvV2SampleBox>
    <CsvV2DownloadTemplate type="button" onClick={onDownloadTemplate}>
      {downloadLabel}
    </CsvV2DownloadTemplate>
  </CsvV2Panel>
)

export const CsvV2UploadPanelBody = ({
  isLoading,
  file,
  onClearFile,
  summaries,
  errors,
  showAttachmentError,
  onBrowse,
  maxErrors = 5,
}: {
  isLoading: boolean
  file: File | null
  onClearFile: () => void
  summaries: Array<{ label: string; value: React.ReactNode }>
  errors: string[]
  showAttachmentError: boolean
  onBrowse: () => void
  maxErrors?: number
}): JSX.Element => {
  const FileTypeIcon = getMimeTypeIcon(file?.type || MIME_TYPE.TEXT_CSV)

  if (isLoading) {
    return <Spinner color="primary" size="l" />
  }

  if (file) {
    return (
      <CsvV2SelectedState>
        <CsvV2FileChip>
          <CsvV2FileTypeIconWrapper>
            <FileTypeIcon width="24px" height="24px" />
          </CsvV2FileTypeIconWrapper>
          <CsvV2FileNameText>{file.name}</CsvV2FileNameText>
          <CsvV2ChipRemoveButton type="button" onClick={onClearFile}>
            <CsvV2CloseIcon />
          </CsvV2ChipRemoveButton>
        </CsvV2FileChip>
        {summaries.map((summary) => (
          <CsvV2SummaryText key={summary.label}>
            {summary.label}:{' '}
            <CsvV2SummaryValue>{summary.value}</CsvV2SummaryValue>
          </CsvV2SummaryText>
        ))}
        {!!errors.length && (
          <CsvV2ErrorList>
            {errors.slice(0, maxErrors).map((error) => (
              <li key={error}>{error}</li>
            ))}
          </CsvV2ErrorList>
        )}
      </CsvV2SelectedState>
    )
  }

  return (
    <CsvV2EmptyUploadState>
      <CsvV2UploadIcon fill={palette.neutral[600]} />
      <CsvV2ModalSubtitle style={{ margin: 0 }}>
        Drag files to upload
      </CsvV2ModalSubtitle>
      <CsvV2ModalSubtitle style={{ margin: 0 }}>or</CsvV2ModalSubtitle>
      <CsvV2BrowseButton type="button" onClick={onBrowse}>
        Browse files
      </CsvV2BrowseButton>
      <CsvV2SupportedText>
        Supported file types: <strong>CSV</strong>
      </CsvV2SupportedText>
      {showAttachmentError && (
        <CsvV2EmptyMessage>Please add a CSV file to upload.</CsvV2EmptyMessage>
      )}
    </CsvV2EmptyUploadState>
  )
}
